/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useRef, useEffect, useState } from 'react';

// Mantine
import {
	Drawer,
	Box,
	Title,
	ScrollArea,
	Container,
} from '@mantine/core';
import { useForm } from '@mantine/form';

// Styles
import classes from './StartMint.module.css';
import clsx from 'clsx';

// Components
import { Stepper } from '@components/molecules/Stepper';
import { Text } from '@components/atoms/Text';
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { Button } from '@components/atoms/Button';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';

// Components : Steps
import { Step1 } from './Steps/Step1';
import { Step2 } from './Steps/Step2';
import { Step3 } from './Steps/Step3';
import { Step4 } from './Steps/Step4';

// I18n
import { useTranslation } from 'react-i18next';

// Configs
import { STEPS } from './StartMint.config';

// Store
import useStore from '@store/index';
import { useMint } from '@features/sourcing/productions/api/mint';

import { useLocation } from 'react-router';
import { DefaultModal } from '@components/molecules/DefaultModal';
import { useMediaQuery } from '@mantine/hooks';
/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const StartMint = (props: {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
	params?: any;
}) => {
	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');
	/* ******************************** CONSTANTS ******************************* */
	const NBR_STEPS = 4;
	/* ******************************** STATES ******************************* */
	const [isOpenModalCancel, setIsOpenModalCancel] =
		useState(false);
	/* ******************************** HOOKS *********************************** */

	const viewport = useRef<HTMLDivElement>(null);

	const [confirmModalOpened, setConfirmModalOpened] =
		useState<boolean>(false);
	const location = useLocation();

	const { t } = useTranslation();
	const {
		setOffsetCreationDatas,
		setProductionsStartMintsActiveStep,
		productionStartMintsActiveStep,
		productionStartMintsSelectedRecords,
		productionsPage,
		productionsPageSize,
		setProductionsStartMintsSelectedRecords,
		setProductionsStartMintsProducer,
		productionStartMintsProducer,
		setProductionsStartMintsQueryParams,
		productionStartMintsQueryParams,
		mintHistorySelected,
	} = useStore();

	const mint = useMint(
		productionsPage,
		productionsPageSize
	);

	// Validators
	const form = useForm({
		initialValues: {
			name: '',
			brand: '',
			tag: '',
			quantity: 0,
			startDate: null,
			endDate: null,
		},
		validate: {
			name: (value) =>
				value.length < 2
					? 'Name must have at least 2 letters'
					: null,
			brand: (value) =>
				value.length == 0 ? 'Brand is required' : null,
			tag: (value) =>
				value.length == 0 ? 'Tag is required' : null,

			quantity: (value) =>
				value == 0 ? 'Quantity cannot be zero' : null,
			startDate: (value) =>
				value == null ? 'Start date is required' : null,

			endDate: (value) =>
				value == null ? 'End date is required' : null,
		},
	});

	useEffect(() => {
		if (form.values) {
			setOffsetCreationDatas({
				...form.values,
				selectedMesurabilityProjects: [],
				selectedAdditionalProjects: [],
			});
		}
	}, [form.values, setOffsetCreationDatas]);

	useEffect(() => {
		setProductionsStartMintsActiveStep(0);
	}, [setProductionsStartMintsActiveStep]);

	/* ******************************** HELPERS ********************************* */
	const getContent: any = () => {
		switch (productionStartMintsActiveStep) {
			case 0:
				return (
					<Step1
						form={form}
						onCancel={function (): void {
							setProductionsStartMintsProducer('');
							setProductionsStartMintsSelectedRecords([]);
							props.onClose();
							setProductionsStartMintsActiveStep(0);
							form.setFieldValue('producer', null);
						}}
						onBack={function (): void {
							throw new Error(
								'Function not implemented.'
							);
						}}
					/>
				);
			case 1:
				return (
					<Step2
						producerId={productionStartMintsProducer}
					/>
				);
			case 2:
				return <Step3 />;
			case 3:
				return <Step4 />;

			default:
				break;
		}
	};

	const handleNextStep = () => {
		if (productionStartMintsActiveStep == 2) {
			if (confirmModalOpened) {
				setProductionsStartMintsActiveStep(
					productionStartMintsActiveStep + 1
				);
				setConfirmModalOpened(false);
				mint.mutate({
					project:
						productionStartMintsSelectedRecords[0]
							.projectId,
					productions:
						productionStartMintsSelectedRecords.map(
							(item: any) => item.id
						),
				});
			} else {
				setConfirmModalOpened(true);
			}
		} else if (productionStartMintsActiveStep == 3) {
			setProductionsStartMintsProducer('');
			setProductionsStartMintsSelectedRecords([]);
			props.onClose();
			setProductionsStartMintsActiveStep(0);
			form.setFieldValue('producer', null);
			if (Object.keys(mintHistorySelected).length !== 0) {
				return;
			}
		} else {
			setProductionsStartMintsActiveStep(
				productionStartMintsActiveStep + 1
			);
		}
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<>
			{/*Mint confirmation modal */}
			<ConfirmationModal
				title={t('productions.modal.mintMany.title')}
				description={t(
					'productions.modal.mint.description'
				)}
				onConfirm={handleNextStep}
				onClose={() => setConfirmModalOpened(false)}
				opened={confirmModalOpened}
			/>
			<Drawer.Root
				position="right"
				size="100%"
				opened={props.opened}
				onClose={() => {
					setProductionsStartMintsProducer('');
					setProductionsStartMintsSelectedRecords([]);
					setIsOpenModalCancel(true);
					setProductionsStartMintsActiveStep(0);
					form.setFieldValue('producer', null);
				}}
			>
				<Drawer.Overlay />
				<Drawer.Content
					ref={viewport}
					className={classes.root}
				>
					<DefaultModal
						opened={isOpenModalCancel}
						onConfirm={() => {
							props.onClose();
							setIsOpenModalCancel(false);
							setProductionsStartMintsProducer('');
							setProductionsStartMintsSelectedRecords([]);
							setProductionsStartMintsActiveStep(0);
							form.setFieldValue('producer', null);
						}}
						onClose={() => {
							setIsOpenModalCancel(false);
						}}
						title={t('global.backToPreviousSure')}
					/>
					{/** Header */}
					<Drawer.Header className={classes.DrawerHeader}>
						<Box className={classes.header}>
							<PreviousDrawerButton
								onClick={() => {
									if (
										Object.keys(mintHistorySelected)
											.length !== 0 ||
										productionStartMintsActiveStep === 3
									) {
										props.onClose();
									} else {
										setIsOpenModalCancel(true);
									}
								}}
							>
								{Object.keys(mintHistorySelected)
									.length !== 0 ||
								location.pathname ==
									'/productions/mint-history'
									? t(
											'mintHistories.backToAllMintHistories'
										)
									: t('productions.backToAllProductions')}
							</PreviousDrawerButton>{' '}
						</Box>
					</Drawer.Header>
					{/** Body */}
					<Drawer.Body className={classes.body}>
						<Box className={classes.containerTitle}>
							<Box className={classes.contentTitle}>
								<Title className={classes.titleForm}>
									{t('productions.title')}
								</Title>
								<Text className={classes.subTitleForm}>
									/ {t('productions.mintProduction')}
								</Text>
							</Box>
						</Box>
						{/** Stepper */}
						<Box className={classes.containerStepper}>
							<Box className={classes.wrapperStepper}>
								<Stepper
									readOnly
									options={STEPS}
									active={productionStartMintsActiveStep}
									iconSize={32}
									onStepClick={() => {}}
								/>
							</Box>
						</Box>
						<Box className={classes.wrapperBody}>
							{getContent()}
						</Box>
					</Drawer.Body>
					{/** Footer */}
					<Box className={classes.containerFooter}>
						<Box className={classes.spacerFooter} />
						<Box className={classes.bodyFooter}>
							<Box
								className={classes.containerButtonsFooter}
							>
								<Box
									onClick={() => {
										if (
											productionStartMintsActiveStep > 0
										)
											setProductionsStartMintsActiveStep(
												productionStartMintsActiveStep - 1
											);
										if (
											productionStartMintsSelectedRecords.length ==
											0
										)
											setProductionsStartMintsQueryParams(
												{
													...productionStartMintsQueryParams,
													project: '',
												}
											);
									}}
									className={clsx(
										classes.previousButton,
										productionStartMintsActiveStep == 0 &&
											classes.previousButtonDisabled
									)}
								>
									{productionStartMintsActiveStep !== 3 &&
										t('global.previous')}
								</Box>
							</Box>
							<Box
								className={classes.containerButtonsFooter}
							>
								<Box
									onClick={() => {
										setProductionsStartMintsProducer('');
										setProductionsStartMintsSelectedRecords(
											[]
										);
										props.onClose();
										setProductionsStartMintsActiveStep(0);
										form.setFieldValue('producer', null);
									}}
									className={classes.cancelButton}
								>
									{productionStartMintsActiveStep !== 3 &&
										t('global.cancel')}
								</Box>
								<Button
									disabled={
										productionStartMintsActiveStep ==
											NBR_STEPS ||
										(productionStartMintsActiveStep ===
											1 &&
											productionStartMintsSelectedRecords.length ==
												0) ||
										(productionStartMintsActiveStep ===
											0 &&
											productionStartMintsProducer ==
												'') ||
										(productionStartMintsActiveStep ===
											2 &&
											productionStartMintsSelectedRecords.length ==
												0)
									}
									fullWidth
									hiddenFrom="xxl"
									size="md"
									onClick={handleNextStep}
								>
									{(productionStartMintsActiveStep == 2 &&
										'Confirm') ||
										(productionStartMintsActiveStep ==
											3 &&
											t('global.ok')) ||
										t('global.next')}
								</Button>

								<Button
									disabled={
										productionStartMintsActiveStep ==
											NBR_STEPS ||
										(productionStartMintsActiveStep ===
											1 &&
											productionStartMintsSelectedRecords.length ==
												0) ||
										(productionStartMintsActiveStep ===
											0 &&
											productionStartMintsProducer ==
												'') ||
										(productionStartMintsActiveStep ===
											2 &&
											productionStartMintsSelectedRecords.length ==
												0)
									}
									fullWidth
									visibleFrom="xxl"
									size="lg"
									type="submit"
									onClick={handleNextStep}
								>
									{(productionStartMintsActiveStep == 2 &&
										'Confirm') ||
										(productionStartMintsActiveStep ==
											3 &&
											t('global.ok')) ||
										t('global.next')}
								</Button>
							</Box>
						</Box>

						<Box className={classes.spacerFooter} />
					</Box>
				</Drawer.Content>
			</Drawer.Root>
		</>
	);
};
